import React, { useState, useEffect, useRef, useCallback } from 'react';
import './VoiceOverlay.css';
import useAzureSpeech from '../../hooks/useAzureSpeech';
import { Mic, MicOff, X, Square, Check } from 'lucide-react';
import { logger } from "../../utils/logger";
import {
  MAX_RECORDING_SECONDS,
  transcriptFlashMs,
  Waveform,
  ModeToggle,
} from './VoiceOverlay.parts';
import type { InteractionMode } from './VoiceOverlay.parts';

const MIC_STATES = {
  CHECKING: 'checking',
  GRANTED: 'granted',
  DENIED: 'denied',
  PROMPT: 'prompt',
  UNAVAILABLE: 'unavailable',
  ERROR: 'error',
} as const;

const VoiceOverlay = ({ isOpen, onClose, onDraftChange }) => {
  const [status, setStatus] = useState<'idle' | 'listening' | 'processing' | 'success' | 'error'>('idle');
  const [micPermission, setMicPermission] = useState<string>(MIC_STATES.CHECKING);
  const [isRequestingMic, setIsRequestingMic] = useState(false);
  const [remaining, setRemaining] = useState(MAX_RECORDING_SECONDS);
  const [interactionMode, setInteractionMode] = useState<InteractionMode>('tap');
  const [lastTranscript, setLastTranscript] = useState('');
  const remainingRef = useRef(MAX_RECORDING_SECONDS);
  const stoppingRef = useRef(false);
  const timerIdRef = useRef(0);
  const stopRef = useRef<(() => void) | null>(null);
  const onDraftChangeRef = useRef(onDraftChange);
  const flashTimerRef = useRef(0);
  const isHoldingRef = useRef(false);
  const statusRef = useRef(status);
  const pendingStopRef = useRef(false);
  statusRef.current = status;
  onDraftChangeRef.current = onDraftChange;

  const [errorMessage, setErrorMessage] = useState('');

  const azure = useAzureSpeech();
  const azureCleanupRef = useRef(azure.cleanup);
  azureCleanupRef.current = azure.cleanup;

  // Translate Azure error codes to user-friendly messages
  const prevAzureErrorRef = useRef<string | null>(null);
  useEffect(() => {
    if (azure.error && azure.error !== prevAzureErrorRef.current) {
      prevAzureErrorRef.current = azure.error;
      const friendlyMessage = ({
        web_speech_unsupported: 'Voice recognition is not supported in this browser. Please use Chrome or Edge.',
        web_speech_error: 'Voice recognition encountered an error. Please try again.',
        web_speech_start_failed: 'Failed to start voice recognition. Please try again.',
        recognition_error: 'Voice recognition was interrupted. Please try again.',
      } as Record<string, string>)[azure.error] ?? 'Voice recognition error. Please try again.';
      setErrorMessage(friendlyMessage);
      setStatus('error');
    } else if (!azure.error) {
      prevAzureErrorRef.current = null;
    }
  }, [azure.error]);

  const checkMicrophonePermission = useCallback(async () => {
    if (!window.isSecureContext) {
      setMicPermission(MIC_STATES.UNAVAILABLE);
      return;
    }
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      setMicPermission(MIC_STATES.UNAVAILABLE);
      return;
    }
    try {
      if (navigator.permissions && navigator.permissions.query) {
        const result = await navigator.permissions.query({ name: 'microphone' });
        if (result.state === 'granted') {
          setMicPermission(MIC_STATES.GRANTED);
        } else if (result.state === 'denied') {
          setMicPermission(MIC_STATES.DENIED);
        } else {
          setMicPermission(MIC_STATES.PROMPT);
        }
        return;
      }
    } catch {
      // permissions.query not supported
    }
    setMicPermission(MIC_STATES.PROMPT);
  }, []);

  const requestMicrophonePermission = useCallback(() => {
    setIsRequestingMic(true);
    navigator.mediaDevices.getUserMedia({ audio: true })
      .then((stream) => {
        stream.getTracks().forEach(track => track.stop());
        setMicPermission(MIC_STATES.GRANTED);
      })
      .catch((error) => {
        logger.error('[VoiceOverlay] Microphone permission error:', error);
        if (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError') {
          setMicPermission(MIC_STATES.DENIED);
        } else if (error.name === 'NotFoundError') {
          setMicPermission(MIC_STATES.UNAVAILABLE);
        } else {
          setMicPermission(MIC_STATES.ERROR);
        }
      })
      .finally(() => {
        setIsRequestingMic(false);
      });
  }, []);

  useEffect(() => {
    if (isOpen) {
      checkMicrophonePermission();
    }
  }, [isOpen, checkMicrophonePermission]);

  useEffect(() => {
    return () => {
      azureCleanupRef.current();
      if (timerIdRef.current) clearInterval(timerIdRef.current);
      if (flashTimerRef.current) clearTimeout(flashTimerRef.current);
    };
  }, []);

  const startTimer = useCallback(() => {
    // Clear any existing interval to prevent leaks if called twice
    // (e.g. double-click before azure.start() resolves)
    if (timerIdRef.current) clearInterval(timerIdRef.current);
    setRemaining(MAX_RECORDING_SECONDS);
    remainingRef.current = MAX_RECORDING_SECONDS;
    stoppingRef.current = false;
    const startedAt = Date.now();
    timerIdRef.current = window.setInterval(() => {
      const sec = Math.floor((Date.now() - startedAt) / 1000);
      const left = Math.max(0, MAX_RECORDING_SECONDS - sec);
      remainingRef.current = left;
      setRemaining(left);
      if (left <= 0 && !stoppingRef.current) {
        stoppingRef.current = true;
        if (stopRef.current) stopRef.current();
      }
    }, 200);
  }, []);

  const cleanupRecording = useCallback(() => {
    if (timerIdRef.current) clearInterval(timerIdRef.current);
  }, []);

  const startRecording = useCallback(async () => {
    logger.log('[VoiceOverlay] Starting Azure browser SDK');
    pendingStopRef.current = false; // clear stale flag from any prior session/retry
    const azureOk = await azure.start();

    if (!azureOk) {
      logger.error('[VoiceOverlay] Azure SDK failed to start');
      isHoldingRef.current = false; // reset so pointer-leave won't fire spurious stop
      setStatus('error');
      return;
    }

    logger.log('[VoiceOverlay] Azure browser SDK active - live transcription enabled');
    startTimer();
    setStatus('listening');

    // If the user released hold before azure.start() resolved, stop immediately
    if (pendingStopRef.current) {
      pendingStopRef.current = false;
      if (stopRef.current) stopRef.current();
    }
  }, [azure, startTimer]);

  const stopRecording = useCallback(async () => {
    stoppingRef.current = true;
    cleanupRecording();
    setStatus('processing');
    const segments = await azure.stop() as string[];

    const accumulated = segments.join(' ').trim();
    logger.log('[VoiceOverlay] Azure SDK stop - final text:', accumulated.slice(0, 80));
    if (accumulated && onDraftChangeRef.current) {
      // Show transcript highlight before merging into input
      setLastTranscript(accumulated);
      setStatus('success');
      onDraftChangeRef.current(accumulated);

      // Auto-dismiss the success flash — longer transcripts get more reading time
      const flashDuration = transcriptFlashMs(accumulated);
      if (flashTimerRef.current) clearTimeout(flashTimerRef.current);
      flashTimerRef.current = window.setTimeout(() => {
        setStatus((prev) => prev === 'success' ? 'idle' : prev);
        setLastTranscript('');
      }, flashDuration);
    } else {
      setStatus('idle');
    }
  }, [azure, cleanupRecording]);

  useEffect(() => {
    stopRef.current = stopRecording;
  }, [stopRecording]);

  // Tap mode handler (also used for error-state retry in both modes)
  const handleMicClick = () => {
    if (status === 'listening') {
      stopRecording();
    } else if (status === 'idle' || status === 'error') {
      // In hold mode, set isHoldingRef so the user can stop via the hold
      // button's onPointerUp (handleHoldEnd). Without this the recording
      // would be unstoppable because handleHoldEnd returns early when
      // isHoldingRef is false and there is no dedicated stop button in
      // the hold-mode listening view.
      if (interactionMode === 'hold') {
        isHoldingRef.current = true;
      }
      startRecording();
    }
  };

  // Hold-to-talk handlers
  const handleHoldStart = useCallback((e: React.PointerEvent<HTMLButtonElement>) => {
    if (interactionMode !== 'hold') return;
    if (status !== 'idle' && status !== 'error') return;
    // Capture pointer so up/leave events fire on THIS element even if DOM swaps
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch { /* Safari iOS may throw */ }
    isHoldingRef.current = true;
    pendingStopRef.current = false; // clear any stale flag from a previous session
    startRecording();
  }, [interactionMode, status, startRecording]);

  const handleHoldEnd = useCallback(() => {
    if (interactionMode !== 'hold') return;
    if (!isHoldingRef.current) return;
    isHoldingRef.current = false;
    if (statusRef.current === 'listening') {
      stopRecording();
    } else {
      // User released before azure.start() resolved; flag for pending stop
      pendingStopRef.current = true;
    }
  }, [interactionMode, stopRecording]);

  // On touch devices, onPointerLeave can fire when a finger drifts slightly
  // off the button. We only treat pointerLeave as a hold-end for mouse input;
  // touch users rely on pointerUp (and the fallback onTouchEnd) instead.
  const handleHoldLeave = useCallback((e: React.PointerEvent<HTMLButtonElement>) => {
    if (e.pointerType === 'touch') return; // ignore finger drift on touch screens
    handleHoldEnd();
  }, [handleHoldEnd]);

  if (!isOpen) return null;

  if (micPermission !== MIC_STATES.GRANTED) {
    const permissionLabel =
      micPermission === MIC_STATES.CHECKING ? 'Checking mic...' :
      micPermission === MIC_STATES.DENIED ? 'Mic blocked — check browser settings' :
      micPermission === MIC_STATES.UNAVAILABLE ? 'No microphone available' :
      micPermission === MIC_STATES.ERROR ? 'Microphone error' :
      'Enable microphone to dictate';

    const canRetry = micPermission === MIC_STATES.PROMPT ||
      micPermission === MIC_STATES.DENIED ||
      micPermission === MIC_STATES.ERROR;

    return (
      <div className="voice-bar">
        <button
          className="voice-bar-close"
          onClick={onClose}
          aria-label="Exit voice mode"
          title="Back to typing"
        >
          <X size={18} />
        </button>
        <div className="voice-bar-center">
          {canRetry ? (
            <button
              className="voice-bar-mic voice-bar-mic-idle"
              onClick={requestMicrophonePermission}
              disabled={isRequestingMic}
              aria-label="Enable microphone"
            >
              {isRequestingMic ? <div className="voice-bar-spinner voice-bar-spinner-light" /> : <Mic size={22} />}
            </button>
          ) : (
            <div className="voice-bar-spinner" />
          )}
          <span className="voice-bar-hint">{permissionLabel}</span>
        </div>
      </div>
    );
  }

  const mins = Math.floor(remaining / 60);
  const secs = remaining % 60;
  const timerDisplay = `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
  const liveText = [...azure.finalSegments, azure.interimText].filter(Boolean).join(' ');

  return (
    <div className="voice-bar">
      <button
        className="voice-bar-close"
        onClick={onClose}
        aria-label="Exit voice mode"
        title="Back to typing"
      >
        <X size={18} />
      </button>

      {/* Hold-to-talk: single persistent button across idle+listening to keep pointer capture */}
      {interactionMode === 'hold' && (status === 'idle' || status === 'listening') && (
        <div className={`voice-bar-center${status === 'listening' ? ' voice-bar-listening' : ''}`}>
          {status === 'listening' && <Waveform />}
          {status === 'listening' && (
            <div className="voice-bar-live-area">
              {liveText ? (
                <p className="voice-bar-live-text">{liveText}</p>
              ) : (
                <p className="voice-bar-live-placeholder">Listening \u2014 release to finish</p>
              )}
            </div>
          )}
          {status === 'listening' && <span className="voice-bar-timer">{timerDisplay}</span>}
          <button
            className={`voice-bar-mic voice-bar-mic-hold${status === 'listening' ? ' voice-bar-mic-holding' : ''}`}
            onPointerDown={handleHoldStart}
            onPointerUp={handleHoldEnd}
            onPointerLeave={handleHoldLeave}
            onTouchEnd={handleHoldEnd}  /* Safari iOS fallback if pointer capture unsupported */
            onContextMenu={(e) => e.preventDefault()}
            aria-label={status === 'listening' ? 'Release to stop' : 'Hold to talk'}
          >
            <Mic size={22} />
          </button>
          {status === 'idle' && (
            <>
              <span className="voice-bar-hint">Press and hold to talk</span>
              <ModeToggle current={interactionMode} onChange={setInteractionMode} />
            </>
          )}
        </div>
      )}

      {/* Tap mode: separate idle and listening blocks (no pointer capture needed) */}
      {interactionMode === 'tap' && status === 'idle' && (
        <div className="voice-bar-center">
          <button
            className="voice-bar-mic voice-bar-mic-idle"
            onClick={handleMicClick}
            aria-label="Start recording"
          >
            <Mic size={22} />
          </button>
          <span className="voice-bar-hint">Tap to start \u2014 I\u2019m all ears</span>
          <ModeToggle current={interactionMode} onChange={setInteractionMode} />
        </div>
      )}

      {interactionMode === 'tap' && status === 'listening' && (
        <div className="voice-bar-center voice-bar-listening">
          <Waveform />
          <div className="voice-bar-live-area">
            {liveText ? (
              <p className="voice-bar-live-text">{liveText}</p>
            ) : (
              <p className="voice-bar-live-placeholder">Listening \u2014 speak naturally\u2026</p>
            )}
          </div>
          <span className="voice-bar-timer">{timerDisplay}</span>
          <button
            className="voice-bar-stop"
            onClick={stopRecording}
            aria-label="Stop recording"
          >
            <Square size={16} fill="currentColor" />
          </button>
        </div>
      )}

      {status === 'processing' && (
        <div className="voice-bar-center">
          <div className="voice-bar-spinner" />
          <span className="voice-bar-hint">Turning your words into text&hellip;</span>
        </div>
      )}

      {status === 'success' && lastTranscript && (
        <div className="voice-bar-center">
          <div className="voice-bar-transcript-flash">
            <Check size={18} className="voice-bar-transcript-check" />
            <p
              className="voice-bar-transcript-text"
              style={{ '--voice-flash-duration': `${transcriptFlashMs(lastTranscript) / 1000}s` } as React.CSSProperties}
            >
              {lastTranscript}
            </p>
          </div>
        </div>
      )}

      {status === 'error' && (
        <div className="voice-bar-center">
          <button
            className="voice-bar-mic voice-bar-mic-error"
            onClick={handleMicClick}
            aria-label="Retry recording"
          >
            <MicOff size={22} />
          </button>
          <span className="voice-bar-hint voice-bar-hint-error">
            {errorMessage || 'Could not start mic. Tap to retry.'}
          </span>
        </div>
      )}

    </div>
  );
};

export default VoiceOverlay;
